"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { Modal, Dialog as RACDialog } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useVisualViewportHeight } from "../../hooks/useVisualViewportHeight";
import { DialogFooter } from "../DialogFooter";
import { DialogHeader } from "../DialogHeader";
import { ErrorBoundary } from "../ErrorBoundary";
import { ModalBackdrop } from "../ModalBackdrop";
import { Tray } from "../Tray";
import type { DialogFooterConfig, DialogHeaderConfig } from "./types";

export interface DialogProps extends Omit<DialogHeaderConfig, "onClose">, DialogFooterConfig {
  /**
   * Whether the dialog is open (controlled mode)
   * When using DialogTrigger, leave this undefined for automatic state management
   * @default undefined
   */
  isOpen?: boolean;
  /**
   * Callback when the dialog is closed (controlled mode)
   * When using DialogTrigger, leave this undefined for automatic state management
   * @default undefined
   */
  onClose?: () => void;
  /**
   * Dialog content
   */
  children: React.ReactNode;
  /**
   * Whether to use a transparent backdrop
   * @default false
   */
  transparentOverlay?: boolean;
  /**
   * Maximum width of the dialog
   * @default "600px"
   */
  maxWidth?: string;
  /**
   * Minimum width of the dialog (desktop only, mobile uses full width)
   * @default "400px"
   */
  minWidth?: string;
  /**
   * Maximum height of the dialog on desktop
   * @default "85vh"
   */
  maxHeight?: string;
  /**
   * Explicit height of the dialog on desktop (overrides maxHeight if set)
   * Content will scroll within this fixed height
   * @default undefined
   */
  height?: string;
  /**
   * Maximum height of the dialog on mobile (tray version)
   * @default "90vh"
   */
  mobileMaxHeight?: string;
  /**
   * Explicit height of the dialog on mobile (overrides mobileMaxHeight if set)
   * Content will scroll within this fixed height
   * @default undefined
   */
  mobileHeight?: string;
  /**
   * Whether to include padding inside the content area
   * @default true
   */
  contentPadding?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Dialog
 *
 * Responsive modal dialog component with backdrop.
 *
 * **Desktop (≥768px):** Centered modal with scale and fade animation
 * **Mobile (<768px):** Uses Tray component for native bottom sheet experience with drag handle
 *
 * Includes optional header with title/back button and footer with action buttons.
 *
 * ## Usage Patterns
 *
 * ### Uncontrolled with DialogTrigger (Recommended)
 * ```tsx
 * import { DialogTrigger, Dialog, Button } from "@texturehq/edges";
 *
 * <DialogTrigger>
 *   <Button>Open Dialog</Button>
 *   <Dialog title="My Dialog">
 *     <p>Dialog content</p>
 *   </Dialog>
 * </DialogTrigger>
 * ```
 *
 * ### Controlled (Advanced)
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)} title="My Dialog">
 *   <p>Dialog content</p>
 * </Dialog>
 * ```
 */
export function Dialog({
  isOpen,
  onClose,
  title,
  headerContent,
  titleAlign = "left",
  hideCloseIcon = false,
  hasBackArrow = false,
  onBack,
  children,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  footerContent,
  transparentOverlay = false,
  maxWidth = "600px",
  minWidth = "400px",
  maxHeight = "85vh",
  height,
  mobileMaxHeight = "90vh",
  mobileHeight,
  contentPadding = true,
  className,
}: DialogProps) {
  // Check if we're on mobile - must be called before any early returns
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Track visual viewport height to handle iOS soft keyboard pushing the tray under the keyboard.
  // Only active on mobile in uncontrolled mode (isOpen===undefined). In controlled mode, Dialog
  // delegates to <Tray> which manages its own visualViewport subscription — enabling both would
  // create duplicate listeners and unnecessary re-renders.
  const viewport = useVisualViewportHeight(isMobile && isOpen === undefined);
  const viewportHeight = viewport?.height ?? null;
  const keyboardOffset = viewport?.keyboardOffset ?? 0;

  // Ref to prevent unwanted scroll on the dialog container (used for mobile uncontrolled mode)
  // When focus moves to an element inside the dialog, the browser may try to
  // scroll the dialog container itself (even with overflow:hidden) to bring
  // the focused element into view. This causes the entire dialog to shift up.
  const mobileDialogRef = useRef<HTMLElement>(null);

  // Reset any unwanted scroll on the mobile dialog container
  const handleMobileDialogScroll = useCallback(() => {
    if (mobileDialogRef.current && mobileDialogRef.current.scrollTop !== 0) {
      mobileDialogRef.current.scrollTop = 0;
    }
  }, []);

  // Ref to prevent unwanted scroll on the desktop dialog container
  const dialogRef = useRef<HTMLElement>(null);

  // Ref for the scrollable content area in the mobile uncontrolled path.
  const mobileScrollContentRef = useRef<HTMLDivElement>(null);

  // On iOS, focusing an input inside a non-body scroll container won't automatically
  // scroll it into view. Listen for focusin and manually scroll after the keyboard settles.
  useEffect(() => {
    const scrollEl = mobileScrollContentRef.current;
    if (!scrollEl || !isMobile) return;

    let focusScrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (focusScrollTimeout) clearTimeout(focusScrollTimeout);
      focusScrollTimeout = setTimeout(() => {
        target.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 320);
    };

    scrollEl.addEventListener("focusin", handleFocusIn);
    return () => {
      scrollEl.removeEventListener("focusin", handleFocusIn);
      if (focusScrollTimeout) clearTimeout(focusScrollTimeout);
    };
  }, [isMobile]);

  // Reset any unwanted scroll on the desktop dialog container
  const handleDialogScroll = useCallback(() => {
    if (dialogRef.current && dialogRef.current.scrollTop !== 0) {
      dialogRef.current.scrollTop = 0;
    }
  }, []);

  // In controlled mode, don't render if closed
  if (isOpen === false) return null;

  const hasHeader = title || headerContent;
  const hasFooter = primaryAction || secondaryAction || tertiaryAction || footerContent;

  // Determine if we're in controlled mode
  const isControlled = isOpen !== undefined;

  // On mobile, use Tray component
  if (isMobile) {
    // In uncontrolled mode (when used with DialogTrigger), we need to return the Tray
    // but let DialogTrigger handle the state. We can't use Tray directly in uncontrolled mode
    // because Tray requires isOpen prop. So we need to wrap it similarly to desktop.
    if (!isControlled) {
      // For uncontrolled mode, render nothing - DialogTrigger will handle rendering
      // This is a limitation: mobile will fall through to desktop rendering for uncontrolled mode
      return (
        <ErrorBoundary>
          <ModalBackdrop
            isDismissable
            transparent={transparentOverlay}
            style={{
              paddingBottom: keyboardOffset > 0 ? `${keyboardOffset}px` : undefined,
              transition: "padding-bottom 0.25s ease-out",
            }}
            className="flex items-end justify-center"
          >
            <Modal className="w-full p-0 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0">
              <RACDialog
                ref={mobileDialogRef}
                onScroll={handleMobileDialogScroll}
                className={twMerge(
                  "relative flex w-full flex-col",
                  "bg-background-surface",
                  "shadow-2xl shadow-black/20",
                  "border-t border-x border-border-default/40",
                  "rounded-t-xl",
                  "overflow-hidden",
                  "animate-in slide-in-from-bottom",
                  "duration-300 ease-out",
                  "data-[exiting]:animate-out data-[exiting]:slide-out-to-bottom",
                  "data-[exiting]:duration-200 data-[exiting]:ease-in",
                  className
                )}
                style={{
                  // Cap height to the visual viewport so the tray stays above the iOS keyboard.
                  // dvh = dynamic viewport height (shrinks with iOS keyboard on Safari 15.4+).
                  // visualViewport JS value = live tracking for older Safari / edge cases.
                  ...(mobileHeight
                    ? { height: mobileHeight }
                    : {
                        // CSS min() only accepts <length-percentage>, not keywords (e.g. "none",
                        // "max-content"). If mobileMaxHeight is a bare keyword, pass it through;
                        // otherwise compose with the live viewport clamp.
                        maxHeight: /\d/.test(mobileMaxHeight)
                          ? viewportHeight !== null
                            ? `min(${mobileMaxHeight}, ${Math.max(viewportHeight - 8, 0)}px, 100dvh)`
                            : `min(${mobileMaxHeight}, 100dvh)`
                          : mobileMaxHeight,
                      }),
                }}
              >
                <div className="flex justify-center pt-2">
                  <div className="h-1.5 w-12 rounded-full bg-border-muted" />
                </div>
                {hasHeader &&
                  (hasBackArrow ? (
                    <DialogHeader
                      title={title}
                      headerContent={headerContent}
                      titleAlign={titleAlign}
                      hideCloseIcon={hideCloseIcon || !onClose}
                      hasBackArrow={true}
                      onBack={onBack!}
                      onClose={
                        onClose ||
                        (() => {
                          /* no-op for uncontrolled mode */
                        })
                      }
                    />
                  ) : (
                    <DialogHeader
                      title={title}
                      headerContent={headerContent}
                      titleAlign={titleAlign}
                      hideCloseIcon={hideCloseIcon || !onClose}
                      onClose={
                        onClose ||
                        (() => {
                          /* no-op for uncontrolled mode */
                        })
                      }
                    />
                  ))}
                <div
                  ref={mobileScrollContentRef}
                  className={twMerge(
                    "flex-1 min-h-0 overflow-y-auto overscroll-contain",
                    contentPadding ? "px-6 py-6" : "p-0"
                  )}
                >
                  {children}
                </div>
                {hasFooter && (
                  <DialogFooter
                    primaryAction={primaryAction}
                    secondaryAction={secondaryAction}
                    tertiaryAction={tertiaryAction}
                    footerContent={footerContent}
                  />
                )}
              </RACDialog>
            </Modal>
          </ModalBackdrop>
        </ErrorBoundary>
      );
    }

    // Controlled mode - use Tray
    return (
      <Tray
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        headerContent={headerContent}
        titleAlign={titleAlign}
        hideCloseIcon={hideCloseIcon}
        hasBackArrow={hasBackArrow}
        onBack={onBack}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        footerContent={footerContent}
        transparentOverlay={transparentOverlay}
        maxHeight={mobileMaxHeight}
        height={mobileHeight}
        maxWidth={maxWidth}
        contentPadding={contentPadding}
        showHandle={true}
        animationVariant="scale"
        className={className}
      >
        {children}
      </Tray>
    );
  }

  // On desktop, use centered modal
  return (
    <ErrorBoundary>
      <ModalBackdrop
        // Only pass isOpen/onOpenChange in controlled mode
        {...(isControlled
          ? {
              isOpen: isOpen,
              onOpenChange: (open) => {
                if (!open && onClose) onClose();
              },
            }
          : {})}
        isDismissable
        transparent={transparentOverlay}
        className="flex items-center justify-center"
      >
        <Modal
          className="w-full p-4 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0"
          style={{ maxWidth }}
        >
          <RACDialog
            ref={dialogRef}
            onScroll={handleDialogScroll}
            className={twMerge(
              // Base structure
              "relative flex w-full flex-col",
              // Visual styling
              "bg-background-surface",
              "shadow-2xl shadow-black/20",
              "border border-border-default/50",
              // Layout
              "rounded-md",
              "overflow-hidden",
              // Remove browser focus outline/ring on the dialog container
              "outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0",
              // Desktop: scale and fade animation
              "animate-in fade-in zoom-in-96 slide-in-from-bottom-2",
              "duration-300 ease-out",
              // Exit animations
              "data-[exiting]:animate-out data-[exiting]:fade-out data-[exiting]:zoom-out-95",
              "data-[exiting]:duration-200 data-[exiting]:ease-in",
              className
            )}
            style={{
              minWidth,
              ...(height ? { height } : { maxHeight }),
            }}
          >
            {hasHeader &&
              (hasBackArrow ? (
                <DialogHeader
                  title={title}
                  headerContent={headerContent}
                  titleAlign={titleAlign}
                  hideCloseIcon={hideCloseIcon || !onClose}
                  hasBackArrow={true}
                  onBack={onBack!}
                  onClose={
                    onClose ||
                    (() => {
                      /* no-op for uncontrolled mode */
                    })
                  }
                />
              ) : (
                <DialogHeader
                  title={title}
                  headerContent={headerContent}
                  titleAlign={titleAlign}
                  hideCloseIcon={hideCloseIcon || !onClose}
                  onClose={
                    onClose ||
                    (() => {
                      /* no-op for uncontrolled mode */
                    })
                  }
                />
              ))}
            <div className={twMerge("flex-1 min-h-0 overflow-y-auto", contentPadding ? "px-6 py-6" : "p-0")}>
              {children}
            </div>
            {hasFooter && (
              <DialogFooter
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                tertiaryAction={tertiaryAction}
                footerContent={footerContent}
              />
            )}
          </RACDialog>
        </Modal>
      </ModalBackdrop>
    </ErrorBoundary>
  );
}

// Export types
export type {
  DialogAction,
  DialogFooterConfig,
  DialogHeaderConfig,
} from "./types";
